import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LabelList } from 'recharts';
import { TrendingUp, DollarSign, CreditCard, FileText, RefreshCw, BarChart3, Calendar, Wallet, ArrowUpRight, ArrowDownRight, FileSpreadsheet, FileDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Revenue = () => {
 const { token, hasPermission, user } = useAuth();
 const { selectedBranch } = useApp();
 
 const [chartData, setChartData] = useState(null);
 const [chartPeriod, setChartPeriod] = useState('monthly');
 const [chartYear, setChartYear] = useState(new Date().getFullYear());
 const [chartMonth, setChartMonth] = useState(new Date().getMonth() + 1);
 const [loading, setLoading] = useState(false);
 const [ownerFilter, setOwnerFilter] = useState('ALL');
 const [owners, setOwners] = useState([]);

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('id-ID').format(amount || 0);
 };

 const formatCurrencyShort = (value) => {
 if (value >= 1000000000) return (value / 1000000000).toFixed(1) + 'M';
 if (value >= 1000000) return (value / 1000000).toFixed(1) + 'jt';
 if (value >= 1000) return (value / 1000).toFixed(0) + 'rb';
 return value;
 };

 // Fetch owners for filter dropdown
 const fetchOwners = async () => {
 try {
 const authToken = localStorage.getItem('token');
 if (!authToken) return;
 
 const userRole = user?.role?.toLowerCase();
 if (userRole === 'owner') {
 if (user?.owner_id) {
 setOwners([{ id: user.owner_id, name: user.full_name || user.username }]);
 setOwnerFilter(String(user.owner_id));
 }
 return;
 }
 
 const params = new URLSearchParams();
 if (selectedBranch && selectedBranch !== 'all') {
 params.append('branch_id', selectedBranch);
 }
 const response = await axios.get(`${API_URL}/api/v2/owners?${params}`, {
 headers: { Authorization: `Bearer ${authToken}` }
 });
 if (response.data.success) {
 setOwners(response.data.data || []);
 }
 } catch (error) {
 console.error('Error fetching owners:', error);
 }
 };

 useEffect(() => {
 const authToken = localStorage.getItem('token');
 if (authToken && user) {
 const userRole = user?.role?.toLowerCase();
 if (userRole === 'owner' && user?.owner_id) {
 setOwners([{ id: user.owner_id, name: user.full_name || user.username }]);
 setOwnerFilter(String(user.owner_id));
 } else {
 fetchOwners();
 setOwnerFilter('ALL');
 }
 }
 }, [selectedBranch, user]);

 const fetchChartData = async () => {
 try {
 setLoading(true);
 const params = new URLSearchParams({
 period: chartPeriod,
 year: chartYear.toString()
 });
 if (chartPeriod === 'monthly') {
 params.append('month', chartMonth.toString());
 }
 if (selectedBranch && selectedBranch !== 'all') {
 params.append('branch_id', selectedBranch);
 }
 if (ownerFilter && ownerFilter !== 'ALL') {
 params.append('owner_id', ownerFilter);
 }
 
 const response = await axios.get(`${API_URL}/api/v2/revenue/chart-data?${params}`, {
 headers: { Authorization: `Bearer ${token}` }
 });
 
 if (response.data.success) {
 setChartData(response.data.data);
 }
 } catch (error) {
 console.error('Error fetching chart data:', error);
 toast.error('Gagal memuat data pendapatan');
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 if (token) {
 fetchChartData();
 }
 }, [chartPeriod, chartYear, chartMonth, token, selectedBranch, ownerFilter]);

 const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#eab308', '#ef4444'];

 const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Ags', 'Sep', 'Okt', 'Nov', 'Des'];


 // ==================== EXPORT HELPERS ====================
 const fmtRupiah = (n) => 'Rp ' + (Number(n) || 0).toLocaleString('id-ID');
 const periodLabel = () => {
   if (chartPeriod === 'yearly') return `Tahunan ${chartYear}`;
   return `${monthNames[chartMonth - 1]} ${chartYear}`;
 };
 const branchLabel = () => (selectedBranch && selectedBranch !== 'all') ? `Cabang ${selectedBranch}` : 'Semua Cabang';
 const ownerLabel = () => {
   if (!ownerFilter || ownerFilter === 'ALL') return 'Semua Owner';
   const o = owners.find(x => String(x.id) === String(ownerFilter));
   return o ? o.name : ownerFilter;
 };

 const exportToExcel = () => {
   if (!chartData) { toast.error('Data belum termuat'); return; }
   const trendRows = chartData.trend_data || [];
   const methodRows = chartData.payment_methods || [];
   const stamp = new Date().toISOString().slice(0, 10);
   const lines = [];
   lines.push(`"Laporan Pendapatan"`);
   lines.push(`"Periode","${periodLabel()}"`);
   lines.push(`"Rentang","${chartData.date_range?.start || ''} - ${chartData.date_range?.end || ''}"`);
   lines.push(`"Cabang","${branchLabel()}"`);
   lines.push(`"Owner","${ownerLabel()}"`);
   lines.push(`"Dicetak","${new Date().toLocaleString('id-ID')}"`);
   lines.push('');
   lines.push('"RINGKASAN"');
   lines.push(`"Total Pendapatan",${chartData.total_revenue || 0}`);
   lines.push(`"Jumlah Transaksi",${chartData.total_transactions || 0}`);
   lines.push(`"Rata-rata per Transaksi",${chartData.avg_transaction || 0}`);
   lines.push(`"Metode Terbanyak","${chartData.top_method?.name || '-'}",${chartData.top_method?.amount || 0}`);
   lines.push('');
   lines.push(`"TREN ${chartPeriod === 'yearly' ? 'BULANAN' : 'HARIAN'}"`);
   lines.push('"Periode","Nominal"');
   trendRows.forEach(r => lines.push(`"${r.name}",${r.amount || 0}`));
   lines.push('');
   lines.push('"BREAKDOWN METODE PEMBAYARAN"');
   lines.push('"Metode","Nominal","Jumlah Transaksi"');
   methodRows.forEach(r => lines.push(`"${r.name}",${r.amount || 0},${r.count || 0}`));
   const csv = "\ufeff" + lines.join("\n");
   const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   a.href = url; a.download = `pendapatan-${chartPeriod}-${chartYear}${chartPeriod==='monthly'?('-'+String(chartMonth).padStart(2,'0')):''}-${stamp}.csv`;
   document.body.appendChild(a); a.click(); a.remove();
   URL.revokeObjectURL(url);
   toast.success('Laporan Pendapatan berhasil di-export ke Excel/CSV');
 };

 const exportToPdf = () => {
   if (!chartData) { toast.error('Data belum termuat'); return; }
   const w = window.open('', '_blank');
   if (!w) { toast.error('Popup diblokir browser'); return; }
   const trendRows = chartData.trend_data || [];
   const methodRows = chartData.payment_methods || [];
   const cell = 'border:1px solid #cbd5e1;padding:6px 8px;font-size:11px';
   const cellHead = 'border:1px solid #cbd5e1;padding:6px 8px;background:#f1f5f9;text-align:left;font-size:11px';
   const summaryHTML = `
     <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin:14px 0">
       <div style="padding:10px 14px;background:#ecfdf5;border-left:4px solid #10b981;border-radius:4px">
         <div style="font-size:11px;color:#6b7280">Total Pendapatan</div>
         <div style="font-size:16px;font-weight:600;color:#065f46">${fmtRupiah(chartData.total_revenue)}</div>
       </div>
       <div style="padding:10px 14px;background:#eff6ff;border-left:4px solid #3b82f6;border-radius:4px">
         <div style="font-size:11px;color:#6b7280">Jumlah Transaksi</div>
         <div style="font-size:16px;font-weight:600;color:#1e40af">${chartData.total_transactions || 0}</div>
       </div>
       <div style="padding:10px 14px;background:#faf5ff;border-left:4px solid #8b5cf6;border-radius:4px">
         <div style="font-size:11px;color:#6b7280">Rata-rata per Transaksi</div>
         <div style="font-size:16px;font-weight:600;color:#6b21a8">${fmtRupiah(chartData.avg_transaction)}</div>
       </div>
       <div style="padding:10px 14px;background:#fffbeb;border-left:4px solid #f59e0b;border-radius:4px">
         <div style="font-size:11px;color:#6b7280">Metode Terbanyak</div>
         <div style="font-size:16px;font-weight:600;color:#92400e">${chartData.top_method?.name || '-'} · ${fmtRupiah(chartData.top_method?.amount)}</div>
       </div>
     </div>`;
   const trendTable = `
     <h3 style="margin:20px 0 8px 0;font-size:13px">Tren ${chartPeriod === 'yearly' ? 'Bulanan' : 'Harian'}</h3>
     <table style="width:100%;border-collapse:collapse">
       <thead><tr><th style="${cellHead}">Periode</th><th style="${cellHead}">Nominal</th></tr></thead>
       <tbody>${trendRows.map(r => `<tr><td style="${cell}">${r.name}</td><td style="${cell};text-align:right">${fmtRupiah(r.amount)}</td></tr>`).join('') || `<tr><td colspan=2 style="${cell};text-align:center;color:#94a3b8">Tidak ada data</td></tr>`}</tbody>
     </table>`;
   const methodTable = `
     <h3 style="margin:20px 0 8px 0;font-size:13px">Breakdown Metode Pembayaran</h3>
     <table style="width:100%;border-collapse:collapse">
       <thead><tr><th style="${cellHead}">Metode</th><th style="${cellHead}">Nominal</th><th style="${cellHead}">Jumlah Transaksi</th></tr></thead>
       <tbody>${methodRows.map(r => `<tr><td style="${cell}">${r.name}</td><td style="${cell};text-align:right">${fmtRupiah(r.amount)}</td><td style="${cell};text-align:right">${r.count || 0}</td></tr>`).join('') || `<tr><td colspan=3 style="${cell};text-align:center;color:#94a3b8">Tidak ada data</td></tr>`}</tbody>
     </table>`;
   w.document.write(`<html><head><title>Laporan Pendapatan</title></head>
     <body style="font-family:Arial,sans-serif;padding:24px;color:#0f172a">
       <div style="border-bottom:2px solid #10b981;padding-bottom:10px;margin-bottom:10px">
         <h2 style="margin:0;color:#065f46">Laporan Pendapatan</h2>
         <div style="font-size:11px;color:#64748b;margin-top:4px">
           Periode: <b>${periodLabel()}</b> · Rentang: ${chartData.date_range?.start || ''} sd ${chartData.date_range?.end || ''} · ${branchLabel()} · ${ownerLabel()}
         </div>
         <div style="font-size:10px;color:#94a3b8;margin-top:2px">Dicetak: ${new Date().toLocaleString('id-ID')}</div>
       </div>
       ${summaryHTML}
       ${trendTable}
       ${methodTable}
       <script>setTimeout(()=>window.print(),300)</script>
     </body></html>`);
   w.document.close();
   toast.success('Laporan Pendapatan siap dicetak / disimpan sebagai PDF');
 };

  return (
    <div className="space-y-4 p-3 sm:p-4 md:p-6 bg-slate-50 dark:bg-slate-950 min-h-screen">
      {/* Header */}
      <div className="flex items-end justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Pendapatan</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Analisis pendapatan berdasarkan pembayaran</p>
        </div>
      </div>

      {/* Filter Card — outline compact */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
        <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 px-4 py-2.5">
          <Calendar className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Filter Periode</span>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select
              value={chartPeriod}
              onChange={(e) => setChartPeriod(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="revenue-period-select"
            >
              <option value="monthly">Bulanan</option>
              <option value="yearly">Tahunan</option>
            </select>
            <input
              type="number"
              value={chartYear}
              onChange={(e) => setChartYear(parseInt(e.target.value))}
              className="h-9 px-3 text-sm border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="revenue-year-input"
            />
            {chartPeriod === 'monthly' ? (
              <select
                value={chartMonth}
                onChange={(e) => setChartMonth(parseInt(e.target.value))}
                className="h-9 px-3 text-sm border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="revenue-month-select"
              >
                {monthNames.map((name, idx) => (
                  <option key={idx} value={idx + 1}>{name}</option>
                ))}
              </select>
            ) : <div />}
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              disabled={user?.role?.toLowerCase() === 'owner'}
              className={`h-9 px-3 text-sm border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${user?.role?.toLowerCase() === 'owner' ? 'opacity-60 cursor-not-allowed' : ''}`}
              data-testid="revenue-owner-select"
            >
              <option value="ALL">Semua Owner</option>
              {owners.map(owner => (
                <option key={owner.id} value={owner.id}>{owner.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button
              onClick={exportToExcel}
              size="sm"
              variant="outline"
              disabled={!chartData || loading}
              className="h-8 px-3 text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950 dark:text-emerald-300"
              data-testid="revenue-export-excel-btn"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
              Excel
            </Button>
            <Button
              onClick={exportToPdf}
              size="sm"
              variant="outline"
              disabled={!chartData || loading}
              className="h-8 px-3 text-xs border-rose-500 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950 dark:text-rose-300"
              data-testid="revenue-export-pdf-btn"
            >
              <FileDown className="w-3.5 h-3.5 mr-1.5" />
              PDF
            </Button>
            <Button
              onClick={fetchChartData}
              size="sm"
              variant="outline"
              className="h-8 px-3 text-xs border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              data-testid="revenue-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
          <div className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-emerald-600 animate-spin dark:border-slate-700"></div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Memuat data...</p>
        </div>
      ) : chartData ? (
        <>
          {/* Summary — outline compact */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              {
                label: 'Total Pendapatan',
                value: `Rp ${formatCurrency(chartData.total_revenue)}`,
                subtitle: `${chartData.date_range?.start || ''} – ${chartData.date_range?.end || ''}`,
                icon: TrendingUp,
                accent: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400',
              },
              {
                label: 'Jumlah Transaksi',
                value: chartData.total_transactions || 0,
                subtitle: 'Pembayaran diterima',
                icon: FileText,
                accent: 'text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400',
              },
              {
                label: 'Rata-rata',
                value: `Rp ${formatCurrency(chartData.avg_transaction || 0)}`,
                subtitle: 'Per transaksi',
                icon: Wallet,
                accent: 'text-violet-600 bg-violet-50 dark:bg-violet-950/40 dark:text-violet-400',
              },
              {
                label: 'Top Metode',
                value: chartData.top_method?.name || '-',
                subtitle: `Rp ${formatCurrency(chartData.top_method?.amount || 0)}`,
                icon: CreditCard,
                accent: 'text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400',
              },
            ].map((card, i) => {
              const Icon = card.icon;
              return (
                <div
                  key={i}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4 hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
                  data-testid={`revenue-summary-card-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium">{card.label}</p>
                      <p className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white mt-1 truncate">{card.value}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 truncate">{card.subtitle}</p>
                    </div>
                    <div className={`p-2 rounded-md flex-shrink-0 ${card.accent}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Charts — outline compact */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Trend Chart */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
              <div className="border-b border-slate-100 dark:border-slate-800 px-4 py-2.5 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {chartPeriod === 'yearly' ? 'Tren Bulanan' : 'Tren Harian'}
                </span>
              </div>
              <div className="p-3">
                <div className="h-[240px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData.trend_data || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis
                        domain={[(dMin) => Math.max(0, Math.floor(dMin * 0.97)), (dMax) => Math.ceil(dMax * 1.03)]}
                        tick={{ fill: '#64748b', fontSize: 10 }}
                        tickFormatter={formatCurrencyShort}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={(value) => ['Rp ' + Number(value).toLocaleString('id-ID'), 'Pendapatan']}
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px' }}
                      />
                      <Area type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Payment Method Pie */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
              <div className="border-b border-slate-100 dark:border-slate-800 px-4 py-2.5 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Metode Pembayaran</span>
              </div>
              <div className="p-3">
                <div className="h-[240px]">
                  {chartData.payment_methods && chartData.payment_methods.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.payment_methods}
                          cx="50%" cy="50%"
                          innerRadius={50} outerRadius={80}
                          paddingAngle={3} dataKey="value" nameKey="name"
                        >
                          {chartData.payment_methods.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => ['Rp ' + Number(value).toLocaleString('id-ID'), '']}
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px' }}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconType="circle" iconSize={8} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400 text-sm dark:text-slate-500">
                      Tidak ada data metode pembayaran
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method Detail Table */}
          {chartData.payment_methods && chartData.payment_methods.length > 0 && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
              <div className="border-b border-slate-100 dark:border-slate-800 px-4 py-2.5 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Detail Metode Pembayaran</span>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {chartData.payment_methods.map((method, index) => (
                  <div key={index} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{method.name}</span>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Rp {formatCurrency(method.value)}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">{method.count || 0} transaksi</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg py-16">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full mb-3">
              <BarChart3 className="w-6 h-6 text-slate-400 dark:text-slate-500" />
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Tidak ada data pendapatan untuk periode ini</p>
            <Button onClick={fetchChartData} variant="outline" size="sm" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Coba Lagi
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Revenue;
